import { Box, Card, CardActionArea, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { useState } from 'react';
import { useDispatch, useSelector} from 'react-redux';
import { getMpowerRedirectUrl } from '../../redux-store/actions/auth';
import { GetPortalLaunchUrlResponse, PortalAccess, PortalCode } from '../../redux-store/types/api-types';
import { RootStateType } from '../../redux-store/reducers';
import { CustomInvestorLayout } from '../commonComponents';
import { SUBROLE, USER_ROLES } from '../../utils/constant';

type InternalApp = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  bg: string;
  action: 'internal';
  path: string;
  hiddenWhenPortalAccess?: PortalAccess[];
  isHidden:boolean
};

type ExternalApp = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  bg: string;
  action: 'external';
  portalCode: PortalCode;
  hiddenWhenPortalAccess?: PortalAccess[];
  isHidden:boolean
};

type AppConfig = InternalApp | ExternalApp;

const apps: AppConfig[] = [
  {
    id: 'rm-portal',
    title: 'RM Portal',
    description: 'Wealth Management & Onboarding Dashboard',
    icon: DashboardOutlinedIcon,
    bg: 'linear-gradient(135deg, #AB932B 0%, #6e5d1c 100%)',
    action: 'internal',
    path: '/dashboard',
    hiddenWhenPortalAccess: ['mpower'],
    isHidden:false
  },
  {
    id: 'mpower',
    title: 'mPower Backoffice',
    description: 'Backoffice Operations & Reporting',
    icon: BusinessCenterOutlinedIcon,
    bg: 'linear-gradient(135deg, #1565c0 0%, #0d3c6e 100%)',
    action: 'external',
    portalCode: 'mpower',
    hiddenWhenPortalAccess: ['portal'],
    isHidden:false
  },
  {
    id: 'sfdc',
    title: 'SFDC',
    description: 'Salesforce CRM Platform',
    icon: CloudOutlinedIcon,
    bg: 'linear-gradient(135deg, #00897b 0%, #004d40 100%)',
    action: 'external',
    portalCode: 'sfdc',
    isHidden:true
  },
  {
    id: 'dms',
    title: 'DMS',
    description: 'Document Management System',
    icon: FolderOpenOutlinedIcon,
    bg: 'linear-gradient(135deg, #6a1b9a 0%, #380063 100%)',
    action: 'external',
    portalCode: 'dms',
    isHidden:true
  },
];

export default function AppLauncher(): JSX.Element {
  const dispatch = useDispatch();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { portalAccess, subRole = '', role = '' } = useSelector((state: RootStateType) => state.auth);
  const effectivePortalAccess: PortalAccess = portalAccess ?? 'portal';
  const visibleApps = apps.filter((app) => !app.isHidden && !app.hiddenWhenPortalAccess?.includes(effectivePortalAccess));
  
  const getRmPortalPath = (): string => {
    if ([USER_ROLES.AMCAPPROVER].includes(role) || [SUBROLE.ADM_CH.toLowerCase()].includes(subRole)) {
      return '/applications-list';
    }
    return '/dashboard';
  };

  const handleCardClick = async (app: AppConfig) => {
    if (loadingId) return;

    if (app.action === 'internal') {
      const path = app.id === 'rm-portal' ? getRmPortalPath() : app.path;
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    if (["sfdc", "dms"].includes(app.portalCode)) {
      // SFDC/DMS aren't wired to a backend yet — no-op until their onboarding endpoints exist.
      return;
    }

    try {
      setLoadingId(app.id);
      const res = await (dispatch(getMpowerRedirectUrl()) as unknown as Promise<GetPortalLaunchUrlResponse>);
      if (res?.redirectUrl) {
        window.open(res.redirectUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // global error modal handles display
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <CustomInvestorLayout>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          // bgcolor: 'grey.50',
          px: { xs: 2, sm: 4 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Box sx={{ mb: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}>
            Welcome
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 1 }}>
            Select an application to continue
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ maxWidth: 760, width: '100%' }} justifyContent="center">
          {visibleApps.map((app) => {
            const Icon = app.icon;
            const isLoading = loadingId === app.id;

            return (
              <Grid item xs={12} sm={6} key={app.id}>
                <Card
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleCardClick(app)}
                    disabled={!!loadingId}
                    sx={{
                      flex: 1,
                      background: app.bg,
                      minHeight:{xs: 140, sm: 160} ,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <CardContent sx={{ p: 3, width: '100%' }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.18)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} sx={{ color: '#fff' }} />
                        ) : (
                          <Icon sx={{ color: '#fff', fontSize: 26 }} />
                        )}
                      </Box>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 0.5,lineHeight: 1.3 }}>
                        {app.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)',lineHeight: 1.4 }}>
                        {app.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      </CustomInvestorLayout>
      );
}
